import { ReorderReviewDialog } from "@/components/reorder";
import { Button, type ButtonProps } from "@/components/ui/button";
import { analytics } from "@/lib/analytics";
import { handleActionError, RETRY_HINT } from "@/lib/errors";
import { normalizeStockReorderLines, summarize } from "@/lib/reorder-diff";
import { addedItems } from "@/lib/stock-item";
import {
  useStockReorderConfirm,
  useStockReorderPreview
} from "@/queries/customer/stock-reorder";
import { useStoreCurrencyCode } from "@/queries/regions";
import type {
  StoreStockReorderResponse,
  StoreStockTrackingItem
} from "@repo/api-sdk";
import { toast, Tooltip } from "@medusajs/ui";
import { useNavigate } from "@tanstack/react-router";
import { ShoppingCart } from "lucide-react";
import { memo, useCallback, useState, type ReactNode } from "react";

import { UpdateCountsDialog } from "./UpdateCountsDialog";

interface StockReorderButtonProps {
  /** The tracked variants to reorder - one id for a single item, all due ids
   *  for "reorder all" / the hero. Rendered disabled when empty. */
  variantIds: string[];
  /** The tracked items behind those variants, for the post-reorder count prompt.
   *  Only the ones actually added to a cart are offered a count update. */
  items: StoreStockTrackingItem[];
  /** Which surface triggered it, for the reorder-decision event (ADR-0003). */
  source: "due_item" | "due_all" | "hero";
  /** Route to /cart once the count prompt is dismissed (hero / reorder-all). */
  navigateToCartOnComplete?: boolean;
  variant?: ButtonProps["variant"];
  size?: ButtonProps["size"];
  className?: string;
  label?: string;
  /** Leading icon; defaults to a cart. Pass `null` to omit. */
  icon?: ReactNode;
  /** Render just `icon`, with `label` as the accessible name and tooltip. */
  iconOnly?: boolean;
  disabled?: boolean;
}

/**
 * Variant-keyed "Reorder" action for opt-in stock tracking (#72 / #73, over
 * api#152) - the sibling of the order-keyed `ReorderButton`. On click it previews
 * (resolve tracked variants against live offers, no cart mutation) and opens the
 * shared {@link ReorderReviewDialog}; confirming re-resolves and adds the
 * available + repriced lines to the seller cart(s), emits `stock_reorder_confirmed`,
 * then offers the light "update your counts" prompt. Because a tracked variant is
 * supplier-agnostic, one reorder can fan out across seller carts - the review
 * dialog shows the resolved supplier per line.
 */
export const StockReorderButton = memo(function StockReorderButton({
  variantIds,
  items,
  source,
  navigateToCartOnComplete = false,
  variant = "default",
  size = "default",
  className,
  label = "Reorder",
  icon,
  iconOnly = false,
  disabled = false
}: StockReorderButtonProps) {
  const [reviewOpen, setReviewOpen] = useState(false);
  const [result, setResult] = useState<StoreStockReorderResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [countOpen, setCountOpen] = useState(false);
  const [countPromptItems, setCountPromptItems] = useState<
    StoreStockTrackingItem[]
  >([]);

  const preview = useStockReorderPreview();
  const confirm = useStockReorderConfirm();
  const currencyCode = useStoreCurrencyCode();
  const navigate = useNavigate();

  const handleClick = useCallback(async () => {
    if (variantIds.length === 0) {
      return;
    }
    setError(null);
    try {
      const res = await preview.mutateAsync(variantIds);
      setResult(res);
      setReviewOpen(true);
    } catch (err) {
      toast.error("Could not prepare your reorder", {
        description: handleActionError(err, {
          boundary: "stock reorder preview",
          fallback: RETRY_HINT
        })
      });
    }
  }, [variantIds, preview]);

  const confirmReorder = useCallback(
    async (quickBuy: boolean) => {
      setError(null);
      try {
        const res = await confirm.mutateAsync(variantIds);
        const lines = normalizeStockReorderLines(res.lines);
        const counts = summarize(lines);
        const cartCount = res.cart_ids.length;

        analytics.stockReorderConfirmed({
          source,
          requested_count: variantIds.length,
          added_count: counts.added,
          repriced_count: counts.repriced,
          unavailable_count: counts.unavailable,
          cart_count: cartCount,
          quick_buy: quickBuy
        });

        setReviewOpen(false);

        if (counts.added > 0 && quickBuy && cartCount > 0) {
          // Quick buy skips the cart page, so no "Added to cart" toast and no
          // count prompt - the navigation is the feedback. A reorder can fan out
          // across seller carts; checkout takes them all at once.
          navigate({
            to: "/checkout",
            search: { cart_ids: res.cart_ids.join(","), step: "information" }
          });
        } else if (counts.added > 0) {
          toast.success(
            `Added ${counts.added} ${counts.added === 1 ? "item" : "items"} to your cart`
          );
          // Offer to refresh counts only for the items actually added.
          setCountPromptItems(addedItems(items, lines));
          setCountOpen(true);
        } else {
          toast.info("Nothing was available to reorder right now");
        }
      } catch (err) {
        setError(
          handleActionError(err, {
            boundary: "stock reorder confirm",
            fallback:
              "We couldn't add these items to your cart. Please try again."
          })
        );
      }
    },
    [variantIds, items, source, confirm, navigate]
  );

  const handleConfirm = useCallback(
    () => confirmReorder(false),
    [confirmReorder]
  );

  const handleQuickBuy = useCallback(
    () => confirmReorder(true),
    [confirmReorder]
  );

  const handleCountComplete = useCallback(() => {
    if (navigateToCartOnComplete) {
      navigate({ to: "/cart" });
    }
  }, [navigateToCartOnComplete, navigate]);

  const trigger = (
    <Button
      type="button"
      variant={variant}
      size={size}
      className={className}
      aria-label={iconOnly ? label : undefined}
      onClick={handleClick}
      disabled={disabled || variantIds.length === 0 || preview.isPending}
    >
      {icon === undefined ? (
        <ShoppingCart className="mr-2 h-4 w-4" aria-hidden="true" />
      ) : (
        icon
      )}
      {!iconOnly && (preview.isPending ? "Preparing…" : label)}
    </Button>
  );

  return (
    <>
      {/* Same tooltip as the order-keyed ReorderButton, so icon-only reorder
          actions behave identically across the site. */}
      {iconOnly ? (
        <Tooltip content={label} side="top">
          {trigger}
        </Tooltip>
      ) : (
        trigger
      )}

      <ReorderReviewDialog
        open={reviewOpen}
        onOpenChange={setReviewOpen}
        lines={result ? normalizeStockReorderLines(result.lines) : []}
        // The reorder response carries the currency its prices are quoted in;
        // fall back to the store region only before a preview has resolved.
        currencyCode={result?.currency_code ?? currencyCode ?? "AUD"}
        isConfirming={confirm.isPending}
        error={error}
        onConfirm={() => handleConfirm()}
        onQuickBuy={() => handleQuickBuy()}
        // No backend support for a partial add on this endpoint yet (ADR-0004)
        // - stays read-only / all-in.
        selectable={false}
      />

      <UpdateCountsDialog
        open={countOpen}
        onOpenChange={setCountOpen}
        items={countPromptItems}
        onComplete={handleCountComplete}
      />
    </>
  );
});
